import React, {useContext, useEffect, useRef, useState} from 'react';
import { useParams } from "react-router";
import {useFetch} from "../../../../../../layout/hooks/useFetch";
import {ProgressSpinner} from "primereact/progressspinner";
import PageHeading from "../../../../../../Components/PageHeading";
import {Toast} from "primereact/toast";
import {Tag} from 'primereact/tag';
import {Button} from 'primereact/button';
import {ProgressBar} from 'primereact/progressbar';
import {webInfo} from "../../../../../../utils/webInfo";
import Label from "../../../../../../Components/Label";
import {InputText} from "primereact/inputtext";
import callToast, {
    handleChangeData,
    handleDelete,
    mergeWithSelected,
    validateFormFields
} from "../../../../../../utils/helper";
import {Dialog} from "primereact/dialog";
import {LayoutContext} from "../../../../../../layout/context/LayoutContext";
import {Dropdown} from "primereact/dropdown";
import {FileUpload, FileUploadHeaderTemplateOptions, FileUploadSelectEvent} from "primereact/fileupload";
import {Tooltip} from "primereact/tooltip";
import {confirmPopup, ConfirmPopup} from "primereact/confirmpopup";

const ManageEmployeeById = () => {
    const initialState = {
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
        role_id: null,
        employee_department_id: null,
        employee_designation_id: null,
    }

    const documentInitialState = {
        employee_document_type_id: null,
        document: null,
    }

    const {employeeId} = useParams<{ employeeId: string }>();
    const [refresh, setRefresh] = useState<boolean>(false);
    const {data: employee, loading, error} = useFetch(`/employees/${employeeId}`, [employeeId, refresh]);
    const toastRef = React.useRef<Toast | null>(null);
    const documentUploadRef = useRef<FileUpload>(null);
    const [profileModal, setProfileModal] = useState<boolean>(false);
    const [formData, setFormData] = useState<any>(initialState);
    const [documentFormData, setDocumentFormData] = useState<any>(documentInitialState);
    const [documentModal, setDocumentModal] = useState<boolean>(false);
    const [isDocumentSubmitting, setIsDocumentSubmitting] = useState<boolean>(false);

    const [roleFilter, setRoleFilter] = useState<string>('');
    const [roleOptions, setRoleOptions] = useState<{ id: number, name: string }[]>([]);

    const [departmentFilter, setDepartmentFilter] = useState<string>('');
    const [departmentOptions, setDepartmentOptions] = useState<{ id: number, name: string }[]>([]);

    const [designationFilter, setDesignationFilter] = useState<string>('');
    const [designationOptions, setDesignationOptions] = useState<{ id: number, name: string }[]>([]);

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const {accessToken} = useContext(LayoutContext)
    const [totalSize, setTotalSize] = useState<number>(0);

    const submitFormEndpointUrl = `/employees/${employeeId}`

    // fetch dropdown data
    const {data: roleRawData} = useFetch(
        `/roles?page=1&per_page=10${roleFilter ? `&name=${roleFilter}` : ''}`,
        [roleFilter]
    );

    useEffect(() => {
        if (roleRawData) {
            const modifiedData = roleRawData?.map((item: any) => ({
                id: item?.id,
                name: item?.name
            }));
            setRoleOptions(mergeWithSelected(modifiedData, formData?.role_id));
        }
    }, [roleRawData, formData?.role_id]);

    const {data: departmentRawData} = useFetch(
        `/employee-departments?page=1&per_page=10${departmentFilter ? `&name=${departmentFilter}` : ''}`,
        [departmentFilter]
    );

    useEffect(() => {
        if (departmentRawData) {
            const modifiedData = departmentRawData?.map((item: any) => ({
                id: item?.id,
                name: item?.name
            }));
            setDepartmentOptions(mergeWithSelected(modifiedData, formData?.employee_department_id));
        }
    }, [departmentRawData, formData?.employee_department_id]);

    const {data: designationRawData} = useFetch(
        `/employee-designations?page=1&per_page=10${designationFilter ? `&name=${designationFilter}` : ''}`,
        [designationFilter]
    );

    useEffect(() => {
        if (designationRawData) {
            const modifiedData = designationRawData?.map((item: any) => ({
                id: item?.id,
                name: item?.name,
            }));
            setDesignationOptions(mergeWithSelected(modifiedData, formData?.employee_designation_id));
        }
    }, [designationRawData, formData?.employee_designation_id]);

    const handlePersonalInfoUpdateSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const validationRules = [
            {field: 'name', label: 'Name'},
            {field: 'email', label: 'Email'},
            {field: 'role_id', label: 'Role'},
            {field: 'employee_department_id', label: 'Department'},
            {field: 'employee_designation_id', label: 'Designation'},
        ];

        if (!validateFormFields(formData, validationRules, toastRef)) return;

        const data = new FormData();

        data.append('name', formData.name);
        data.append('email', formData.email);
        data.append('role_id', String(formData.role_id?.id || ''));
        data.append('employee_department_id', String(formData.employee_department_id?.id || ''));
        data.append('employee_designation_id', String(formData.employee_designation_id?.id || ''));

        try {
            setIsSubmitting(true);
            const response = await fetch(`${webInfo?.baseUrl}${submitFormEndpointUrl}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: data,
            });

            if (response.ok) {
                callToast(toastRef, true, `Employee updated successfully`);
                setProfileModal(false)
                setRoleFilter('')
                setDepartmentFilter('')
                setDesignationFilter('')
                setFormData(initialState);
                return;
            } else {
                const resData = await response.json();
                callToast(toastRef, false, resData.message || resData.error || "Submission failed");
            }

        } catch (err) {
            callToast(toastRef, false, "A network error occurred");
        } finally {
            setIsSubmitting(false);
        }
    };

    const deleteId = useRef<number>(0)


    const accept = () => {
        const deleteParams = {
            id: deleteId.current,
            startEndpoint: "/employees/",
            finalEndpoint: "/delete-document",
            name: "Document",
            setUpdate: setRefresh,
            token: accessToken,
            toast: toastRef,
        }

        handleDelete(deleteParams).then()
    }

    if (loading) return (
        <div className="flex justify-content-center align-items-center h-30rem">
            <ProgressSpinner style={{width: '40px', height: '40px'}} strokeWidth="4"/>
        </div>
    );

    if (error || !employee) return (
        <div className="p-6 text-center">
            <i className="pi pi-user-minus text-400 text-5xl mb-3"></i>
            <div className="text-900 font-medium text-xl">Profile Not Found</div>
        </div>
    );

    const totalDocs = employee.employee_designation?.document_requirements?.length || 0;
    const uploadedDocs = employee.documents?.length || 0;
    const completionPercent = totalDocs > 0 ? Math.round((uploadedDocs / totalDocs) * 100) : 0;

    function message(msg: string, status: string) {
        return <span className={`custom-badge ${status}`}>{msg}</span>;
    }

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    const onTemplateSelect = (e: FileUploadSelectEvent) => {
        let files = e.files;
        let _totalSize = totalSize;
        for (let i = 0; i < files.length; i++) {
            _totalSize += files[i].size || 0;
        }

        setTotalSize(_totalSize);
        handleChangeData('document', e.files[0], documentFormData, setDocumentFormData);
    };

    const onTemplateClear = () => {
        if (documentFormData?.document) {
            handleChangeData('document', null, documentFormData, setDocumentFormData);
            if (documentUploadRef.current) {
                documentUploadRef.current.setFiles([])
            }
        }
    };

    const headerTemplate = (options: FileUploadHeaderTemplateOptions) => {
        const {className, chooseButton} = options;
        return (
            <div className={className} style={{backgroundColor: 'transparent', display: 'flex', alignItems: 'center'}}>
                {chooseButton}
                <Button
                    icon="pi pi-times"
                    className="custom-cancel-btn p-button-danger p-button-rounded p-button-outlined ml-2"
                    disabled={!documentFormData.document}
                    onClick={onTemplateClear}
                    type="button"
                />
            </div>
        );
    };

    const itemTemplate = (file: object) => {
        const fileRef = file as File;
        const fileSize = formatFileSize(fileRef.size || 0);

        // Determine the type of file
        const isImage = fileRef.type.startsWith('image/');
        const isPdf = fileRef.type === 'application/pdf';

        const renderFilePreview = () => {
            if (isImage) {
                return (
                    <img
                        alt={fileRef.name}
                        role="presentation"
                        src={(fileRef as any).objectURL} // PrimeReact FileUpload adds objectURL automatically
                        width={40}
                        height={40}
                        className="flex-shrink-0 border-round"
                        style={{ objectFit: 'cover' }}
                    />
                );
            }

            if (isPdf) {
                return (
                    <i className="pi pi-file-pdf text-red-500 flex-shrink-0" style={{ fontSize: '2rem' }}></i>
                );
            }

            // Default icon for other file types
            return (
                <i className="pi pi-file text-primary flex-shrink-0" style={{ fontSize: '2rem' }}></i>
            );
        };

        return (
            <div className="flex align-items-center w-full p-2 ">
                <div className="flex align-items-center" style={{ flex: '1', minWidth: '0' }}>
                    {renderFilePreview()}

                    <div className="flex flex-column ml-3" style={{ minWidth: '0' }}>
                    <span className="font-semibold text-900" style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block'
                    }}>
                        {fileRef.name}
                    </span>
                        <small className="text-secondary md:hidden">Size: {fileSize}</small>
                    </div>
                </div>

                <div className="flex align-items-center ml-auto">
                    <Tag value={fileSize} severity="warning" className="hidden md:block" />
                </div>
            </div>
        );
    };

    const emptyTemplate = () => (
        <div className="flex flex-column align-items-center justify-content-center py-5">
            <i className="pi pi-upload text-4xl mb-3" style={{color: 'var(--surface-d)'}} />
            <span className="text-lg text-700">Drag and drop or click to select a document</span>
        </div>
    );

    const chooseOptions = {
        icon: 'pi pi-fw pi-images',
        iconOnly: true,
        className: 'custom-choose-btn p-button-rounded p-button-outlined'
    };

    const cancelOptions = {
        icon: 'pi pi-fw pi-times',
        iconOnly: true,
        className: 'custom-cancel-btn p-button-danger p-button-rounded p-button-outlined',
        disabled: !documentFormData?.document
    };



    // Conditional upload/update handler
    // In your handleDocumentUploadSubmit:
    const handleDocumentUploadSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!documentFormData.document) {
            callToast(toastRef, false, "Please select a file to upload.");
            return;
        }

        const data = new FormData();
        data.append('document_type_id', String(documentFormData.employee_document_type_id));
        data.append('document', documentFormData.document);

        // Find if document exists for this type
        const existingDoc = employee.documents?.find(
            (d: any) => d.employee_document_type_id === documentFormData.employee_document_type_id
        );

        // Use employeeId for upload, documentId for update
        const url = existingDoc
            ? `${webInfo.baseUrl}/employees/${existingDoc.id}/update-document`
            : `${webInfo.baseUrl}/employees/${employeeId}/upload-document`;

        try {
            setIsDocumentSubmitting(true);
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: data,
            });

            if (response.ok) {
                callToast(toastRef, true, existingDoc ? "Document updated successfully" : "Document uploaded successfully");
                setDocumentModal(false);
                setDocumentFormData(documentInitialState);
                setTotalSize(0);
                setRefresh(prev => !prev);
                if (documentUploadRef.current) documentUploadRef.current.clear();
            } else {
                const resData = await response.json();
                callToast(toastRef, false, resData.message || "Upload/Update failed");
            }
        } catch (err) {
            callToast(toastRef, false, "A network error occurred");
        } finally {
            setIsDocumentSubmitting(false);
        }
    };


    return (
        <div className="card ">
            <Toast ref={toastRef}/>
            <ConfirmPopup/>
            <PageHeading title="Manage Employee" back={true}/>

            {/* Section 1: Personal Information */}
            <div className="p-5 border-bottom-1 surface-border">
                <div className="flex align-items-center justify-content-between mb-4">
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-user text-blue-500 font-bold"></i>
                        <h2 className="text-xl font-bold text-900 m-0">Personal Information</h2>
                    </div>
                    <Button onClick={() => {
                        setProfileModal(true)
                        setFormData({
                            name: employee.name,
                            email: employee.email,
                            role_id: employee.roles?.[0] ? {
                                id: employee.roles?.[0].id,
                                name: employee.roles?.[0].name
                            } : null,
                            employee_department_id: employee.employee_department,
                            employee_designation_id: employee.employee_designation ? {
                                id: employee.employee_designation.id,
                                name: employee.employee_designation.name
                            } : null,
                        })
                    }} label="Edit Profile" icon="pi pi-pencil" className="p-button-text p-button-sm"/>
                </div>

                <div className="flex flex-column md:flex-row align-items-start gap-5">
                    <div className="flex-1">
                        <div className="grid grid-nogutter row-gap-4">
                            <div className="col-12 md:col-6 lg:col-4">
                                <span className="block text-500 text-xs font-bold uppercase mb-1 tracking-wider">Full Name</span>
                                <div className="flex align-items-center gap-2">
                                    <span className="text-900 font-semibold">{employee.name}</span>
                                    {employee.active_status === 1 ? message('Active', 'approved') : message('Inactive', 'cancelled')}
                                </div>
                            </div>
                            <div className="col-12 md:col-6 lg:col-4">
                                <span className="block text-500 text-xs font-bold uppercase mb-1 tracking-wider">Email Address</span>
                                <span className="text-900 font-semibold">{employee.email}</span>
                            </div>
                            <div className="col-12 md:col-6 lg:col-4">
                                <span className="block text-500 text-xs font-bold uppercase mb-1 tracking-wider">Department</span>
                                <span className="text-900 font-semibold">{employee.employee_department?.name || 'N/A'}</span>
                            </div>
                            <div className="col-12 md:col-6 lg:col-4">
                                <span className="block text-500 text-xs font-bold uppercase mb-1 tracking-wider">Designation</span>
                                <span className="text-900 font-semibold">{employee.employee_designation?.name || 'N/A'}</span>
                            </div>
                            <div className="col-12 md:col-6 lg:col-4">
                                <span className="block text-500 text-xs font-bold uppercase mb-1 tracking-wider">Roles</span>
                                <div className="flex flex-wrap gap-2 mt-1">
                                    {employee.roles?.length > 0 ? employee.roles.map((role: any) => (
                                        <Tag key={role.id} value={role.name}
                                             className="bg-blue-50 text-blue-700 font-medium border-none"/>
                                    )) : <span className="text-900 font-medium">-</span>}
                                </div>
                            </div>
                            <div className="col-12 md:col-6 lg:col-4">
                                <span className="block text-500 text-xs font-bold uppercase mb-1 tracking-wider">Employee ID</span>
                                <span className="text-900 font-semibold">{employeeId}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section 2: Documents */}
            <div className="p-5 bg-surface-0">
                <div className="flex flex-column md:flex-row align-items-start md:align-items-center justify-content-between mb-4 gap-3">
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-file text-blue-500 font-bold"></i>
                        <h2 className="text-xl font-bold text-900 m-0">Employee Documents</h2>
                    </div>
                    <div className="w-full md:w-15rem">
                        <div className="flex justify-content-between text-xs mb-1">
                            <span className="text-600 font-medium">Documents Uploaded</span>
                            <span className="text-900 font-bold">{uploadedDocs}/{totalDocs}</span>
                        </div>
                        <ProgressBar value={completionPercent} showValue={false} style={{height: '6px'}}/>
                    </div>
                </div>

                <div className="surface-border border-1 border-round-lg overflow-hidden">
                    {employee.employee_designation?.document_requirements?.length > 0 ? (
                        employee.employee_designation.document_requirements.map((req: any, index: number) => {
                            const doc = employee.documents?.find((d: any) => d.employee_document_type_id === req.employee_document_type_id);
                            const isRequired = req.is_required === 1;

                            return (
                                <div key={req.id}
                                     className={`flex flex-column sm:flex-row align-items-start sm:align-items-center p-4 hover:surface-50 transition-colors ${index !== totalDocs - 1 ? 'border-bottom-1 surface-border' : ''}`}>

                                    <div className="flex align-items-center flex-1 w-full">
                                        <div className="mr-3">
                                            <div
                                                className={`w-3rem h-3rem flex align-items-center justify-content-center border-round-lg ${doc ? 'bg-green-50 text-green-600' : 'bg-orange-50 text-orange-600'}`}>
                                                <i className={`pi ${doc ? 'pi-file-check' : 'pi-file-o'} text-xl`}></i>
                                            </div>
                                        </div>

                                        <div className="flex-1">
                                            <div className="flex align-items-center gap-2">
                                                <span
                                                    className="font-bold text-900">{req.employee_document_type?.name}</span>
                                                {isRequired && <Tag value="Required"
                                                                    className="bg-red-50 text-red-600 border-none px-2"
                                                                    style={{height: '18px', fontSize: '10px'}}/>}
                                            </div>
                                            <div className="text-xs text-500 mt-1">
                                                {doc ? (
                                                    <span className="flex align-items-center gap-1">
                                                            <i className="pi pi-calendar text-xs"></i>
                                                            Updated {new Date(doc.updated_at).toLocaleDateString()}
                                                        </span>
                                                ) : 'Status: Pending Upload'}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex gap-2 mt-3 sm:mt-0 w-full sm:w-auto justify-content-end">
                                        {doc?.document && (
                                            <a href={`${webInfo.baseUrl}/${doc.document}`} target="_blank"
                                               rel="noreferrer" className="no-underline">
                                                <Button label="View" icon="pi pi-eye"
                                                        className="p-button-outlined p-button-secondary p-button-sm px-3"/>
                                            </a>
                                        )}

                                        <Button
                                            label={doc ? "Update" : "Upload"}
                                            icon={doc ? "pi pi-refresh" : "pi pi-upload"}
                                            className={`p-button-sm px-3 p-button-outlined ${doc ? 'p-button-warning' : 'p-button-success'}`}
                                            onClick={() => {
                                                console.log(req)
                                                setDocumentModal(true)
                                                setDocumentFormData({
                                                    employee_document_type_id: req.employee_document_type_id,
                                                    document: null,
                                                })
                                            }}
                                        />

                                        {doc && !isRequired && (
                                            <Button
                                                label="Delete"
                                                icon="pi pi-trash"
                                                size={"small"}
                                                outlined={true}
                                                // className="p-button-text p-button-danger p-button-outlined p-button-sm px-3"
                                                onClick={(event: any) => {
                                                    deleteId.current = doc.id;
                                                    confirmPopup({
                                                        target: event.currentTarget,
                                                        message: `Do you want to delete this document?`,
                                                        icon: 'pi pi-info-circle',
                                                        acceptClassName: 'p-button-danger',
                                                        accept,
                                                        onHide() {
                                                            deleteId.current = 0;
                                                        }
                                                    })
                                                }}
                                            />

                                        )}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="p-5 text-center text-500 italic">
                            No document requirements found for this designation.
                        </div>
                    )}
                </div>
            </div>

            <Dialog visible={profileModal}
                    style={{width: "50rem"}}
                    headerClassName="text-800 font-semibold text-xl"
                    header={`Edit Profile`}
                    breakpoints={{"960px": "75vw", "640px": "90vw"}}
                    onHide={() => {
                        setProfileModal(false)
                        setFormData(initialState)
                        setRoleFilter('')
                        setDepartmentFilter('')
                        setDesignationFilter('')
                    }}
            >
                <div>
                    <form
                        onSubmit={handlePersonalInfoUpdateSubmit}
                    >
                        <div className="grid">
                            <div className="col-12 md:col-5">
                                <Label label={"Name"} required/>
                                <InputText
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => handleChangeData('name', e.target.value, formData, setFormData)}
                                    placeholder={"Enter name"}
                                    className={`w-full`}
                                />
                            </div>

                            <div className="col-12 md:col-5">
                                <Label label={"Email"} required/>
                                <InputText
                                    id="email"
                                    value={formData.email}
                                    onChange={(e) => handleChangeData('email', e.target.value, formData, setFormData)}
                                    placeholder={"Enter email"}
                                    className={`w-full`}
                                    type="email"
                                />
                            </div>
                        </div>

                        <div className="grid mt-2">
                            <div className="col-12 md:col-4">
                                <Label label={"Employee role"} required/>
                                <Dropdown
                                    options={roleOptions ? roleOptions : []}
                                    optionLabel="name"
                                    id="role_id"
                                    name={`role_id`}
                                    className={`w-full`}
                                    value={formData?.role_id}
                                    onChange={(e) => handleChangeData('role_id', e.value, formData, setFormData)}
                                    placeholder="Select role"
                                    filter
                                    onFilter={(e) => {
                                        if (timeoutRef?.current) {
                                            clearTimeout(timeoutRef.current);
                                        }
                                        timeoutRef.current = setTimeout(() => {
                                            setRoleFilter(e.filter)
                                        }, 500);

                                        if (roleRawData) {
                                            const modifiedData = roleRawData?.map((item: any) => ({
                                                id: item?.id,
                                                name: item?.name
                                            }));
                                            setRoleOptions(modifiedData);
                                        }
                                    }}
                                    showClear
                                    showFilterClear
                                />
                            </div>
                            <div className="col-12 md:col-4">
                                <Label label={"Department"} required/>
                                <Dropdown
                                    options={departmentOptions ? departmentOptions : []}
                                    id="employee_department_id"
                                    optionLabel="name"
                                    name={`employee_department_id`}
                                    className={`w-full`}
                                    value={formData?.employee_department_id}
                                    onChange={(e) => handleChangeData('employee_department_id', e.value, formData, setFormData)}
                                    placeholder="Select department"
                                    filter
                                    onFilter={(e) => {
                                        if (timeoutRef?.current) {
                                            clearTimeout(timeoutRef.current);
                                        }
                                        timeoutRef.current = setTimeout(() => {
                                            setDepartmentFilter(e.filter)
                                        }, 500);
                                        if (departmentRawData) {
                                            const modifiedData = departmentRawData?.map((item: any) => ({
                                                id: item?.id,
                                                name: item?.name
                                            }));
                                            setDepartmentOptions(modifiedData);
                                        }
                                    }}
                                    showClear
                                    showFilterClear
                                />
                            </div>
                            <div className="col-12 md:col-4">
                                <Label label={"Designation"} required/>
                                <Dropdown
                                    options={designationOptions ? designationOptions : []}
                                    id="employee_designation_id"
                                    optionLabel="name"
                                    name={`employee_designation_id`}
                                    className={`w-full`}
                                    value={formData?.employee_designation_id}
                                    onChange={(e) => handleChangeData('employee_designation_id', e.value, formData, setFormData)}
                                    placeholder="Select designation"
                                    filter
                                    onFilter={(e) => {
                                        if (timeoutRef?.current) {
                                            clearTimeout(timeoutRef.current);
                                        }
                                        timeoutRef.current = setTimeout(() => {
                                            setDesignationFilter(e.filter)
                                        }, 500);
                                        if (designationRawData) {
                                            const modifiedData = designationRawData?.map((item: any) => ({
                                                id: item?.id,
                                                name: item?.name,
                                            }))
                                            setDesignationOptions(modifiedData);
                                        }
                                    }}
                                    showClear
                                    showFilterClear
                                />
                            </div>
                        </div>

                        <div className="flex gap-2 mt-2">
                            <Button
                                type="submit"
                                label={"Update"}
                                className="p-button-primary"
                                size={'small'}
                                loading={isSubmitting}
                            />
                        </div>
                    </form>
                </div>
            </Dialog>

            <Dialog visible={documentModal}
                    style={{width: "50rem"}}
                    headerClassName="text-800 font-semibold text-xl"
                    header={
                        employee.documents?.find(
                            (d: any) => d.employee_document_type_id === documentFormData.employee_document_type_id
                        )
                            ? "Update Document"
                            : "Upload Document"
                    }

                    breakpoints={{"960px": "75vw", "640px": "90vw"}}
                    onHide={() => {
                        setDocumentModal(false)
                        setDocumentFormData(documentInitialState)
                        setTotalSize(0)
                        if (documentUploadRef.current) documentUploadRef.current.clear();
                    }}
            >
                <form onSubmit={handleDocumentUploadSubmit}>
                    <Tooltip target=".custom-choose-btn" content="Choose" position="bottom"/>
                    <Tooltip target=".custom-cancel-btn" content="Clear" position="bottom"/>
                    <Label label={employee.employee_designation?.document_requirements?.find(
                        (req: any) => req.employee_document_type_id === documentFormData.employee_document_type_id
                    )?.employee_document_type?.name || "Document Upload"} required/>
                    <FileUpload
                        ref={documentUploadRef}
                        accept="application/pdf,image/*"
                        maxFileSize={1000000}
                        onSelect={onTemplateSelect}
                        onError={onTemplateClear}
                        onClear={onTemplateClear}
                        headerTemplate={headerTemplate}
                        itemTemplate={itemTemplate}
                        emptyTemplate={emptyTemplate}
                        chooseOptions={chooseOptions}
                        cancelOptions={cancelOptions}
                    />
                    <div className="flex gap-2 mt-2">
                        <Button
                            type="submit"
                            label={employee.documents?.find(
                                (d: any) => d.employee_document_type_id === documentFormData.employee_document_type_id
                            ) ? "Update" : "Upload"}
                            className="p-button-primary"
                            size={'small'}
                            loading={isDocumentSubmitting}
                            disabled={!documentFormData.document}
                        />
                    </div>
                </form>
            </Dialog>
        </div>
    );
};

export default ManageEmployeeById;
